import { useState, useEffect } from "react";
import { X } from "react-feather";
import { AxiosError } from "axios";
import {
  programService,
  JoinProgramResponse,
  SwitchProgramSummary,
} from "../../services/program.service";
import { toast } from "react-hot-toast";
import { SwitchProgramModal } from "./SwitchProgramModal";
import { Button } from "../ui/button";

interface JoinErrorBody {
  message?: string;
  code?: string;
  data?: {
    status?: string;
    code?: string;
    amount?: number;
    currentProgram?: SwitchProgramSummary;
    targetProgram?: SwitchProgramSummary;
  };
}

interface JoinBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCode?: string;
  username: string;
  onSuccess: (response: JoinProgramResponse) => void;
  // Called when the backend reports the affiliate is already enrolled, so the
  // parent can reconcile its UI instead of leaving a dead "Join" button up.
  onAlreadyJoined?: (status?: string) => void;
  programId: string;
  // Share token of the program being joined (`?p=` from the join link). Absent
  // means the brand's default program — the pre-share-link behaviour.
  programToken?: string;
  attributionMethod?: 'STATIC_CODE' | 'SNEAKYLINK';
  brandName?: string;
  // When true and the program is SNEAKYLINK, the affiliate picks a wrapper code
  // (e.g. JOHN25) that becomes a Shopify URL redirect to /?rs_token=<token>.
  shortDiscountUrl?: boolean;
  // Token from a TikTok ad-request link; pre-approves the join.
  adRequestToken?: string;
}

export function JoinBrandModal({
  isOpen,
  onClose,
  defaultCode,
  username,
  onSuccess,
  onAlreadyJoined,
  programId,
  programToken,
  attributionMethod = 'STATIC_CODE',
  brandName,
  shortDiscountUrl = false,
  adRequestToken,
}: JoinBrandModalProps) {
  // For sneakylink brands with the short-URL feature on, the affiliate still
  // chooses a code — but it's a URL wrapper, not a real discount code.
  const usesSneakylinkWrapper =
    attributionMethod === 'SNEAKYLINK' && shortDiscountUrl;
  const collectsCode =
    attributionMethod === 'STATIC_CODE' || usesSneakylinkWrapper;
  const [code, setCode] = useState(defaultCode || "");
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  // Set when the join was refused because it would move the affiliate out of
  // another program of this brand. Holds both sides of the comparison.
  const [switchPrompt, setSwitchPrompt] = useState<{
    from: SwitchProgramSummary;
    to: SwitchProgramSummary;
  } | null>(null);

  useEffect(() => {
    setCode(defaultCode || "");
    setIsEditing(false);
    setError("");
  }, [defaultCode]);

  const checkCodeAvailability = async (
    codeToCheck: string,
    programId: string
  ) => {
    if (!codeToCheck || !programId) return;

    try {
      setIsCheckingCode(true);
      const response = await programService.checkCodeAvailability(
        codeToCheck,
        programId
      );
      if (!response.available) {
        setError("This code is not available");
      } else {
        setError("");
      }
    } catch (err) {
      console.error("Code check error:", err);
      setError("Failed to check code availability");
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleCodeChange = (newCode: string) => {
    // Remove spaces and convert to uppercase
    const formattedCode = newCode.replace(/\s+/g, "").toUpperCase();
    setCode(formattedCode);
    setError("");

    if (formattedCode.length >= 3) {
      checkCodeAvailability(formattedCode, programId);
    }
  };

  const handleSave = async (confirmSwitch = false) => {
    if (collectsCode && (error || !code || !username)) {
      toast.error("Please provide a valid code");
      return;
    }

    if (!username) {
      toast.error("Brand username is required");
      return;
    }

    try {
      setIsLoading(true);
      // STATIC_CODE: code is the discount code.
      // SNEAKYLINK + shortDiscountUrl: code is the URL wrapper (e.g. JOHN25).
      // SNEAKYLINK without shortDiscountUrl: no code.
      const codeToSend = collectsCode ? code : '';
      const response = await programService.applyToProgram(
        codeToSend,
        username,
        programToken,
        confirmSwitch,
        adRequestToken
      );
      setSwitchPrompt(null);
      // onSuccess owns the user-facing confirmation — it knows whether this was
      // an enrollment (APPROVED) or an application (PENDING).
      onSuccess(response);
    } catch (err) {
      console.error("Join error:", err);
      const body = (err as AxiosError<JoinErrorBody>)?.response?.data;

      // Already enrolled: the join actually went through on an earlier attempt.
      // Tell the truth and let the parent flip to the joined view.
      if (body?.code === "ALREADY_ENROLLED") {
        toast.success("You're already part of this program");
        onAlreadyJoined?.(body?.data?.status);
        return;
      }

      // They're in another program of this brand. One program per brand, so
      // this is a move — show what it costs them and re-submit on confirm.
      if (
        body?.code === "SWITCH_REQUIRED" &&
        body.data?.currentProgram &&
        body.data?.targetProgram
      ) {
        setSwitchPrompt({
          from: body.data.currentProgram,
          to: body.data.targetProgram,
        });
        return;
      }

      // Switching would strand a payout the brand still owes them.
      if (body?.code === "PENDING_PAYOUT") {
        setSwitchPrompt(null);
        setError(body.message || "You have a pending payout with this brand.");
        toast.error(body.message || "You have a pending payout with this brand.");
        return;
      }

      // Scoped (white-label) affiliate trying to join a brand that isn't
      // their home brand — the backend refuses by design (ticket 3's
      // HomeBrandJoinGuard). Say so plainly instead of "try again".
      if (body?.code === "MARKETPLACE_DISABLED") {
        const scopedMessage = "This brand isn't available on your portal.";
        setError(scopedMessage);
        toast.error(scopedMessage);
        return;
      }

      const message =
        body?.message || "Failed to join program. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-semibold mb-2">
              {attributionMethod === 'SNEAKYLINK' ? 'Join Program' : 'Join Program'}
            </h2>
            <p className="text-muted-foreground text-sm">
              {usesSneakylinkWrapper
                ? `Choose a short code`
                : attributionMethod === 'SNEAKYLINK'
                ? `${brandName || 'This brand'} uses unique sneakylinks for tracking`
                : 'Choose your affiliate code to join this program'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {attributionMethod === 'SNEAKYLINK' && !usesSneakylinkWrapper ? (
          /* Sneakylink Info Section */
          <div className="mb-6">
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-brand-900 mb-1">
                    About Sneakylinks
                  </h3>
                  <p className="text-sm text-brand-800">
                    You'll receive a unique sneakylink to share with your audience.
                    Each click generates a single-use discount code for better tracking
                    and attribution.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm text-brand-700">
                    <li>✓ No static discount code needed</li>
                    <li>✓ Unique codes for each customer</li>
                    <li>✓ Better tracking and analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Code input for STATIC_CODE or SNEAKYLINK + shortDiscountUrl wrapper */
        <div className="mb-6">
          <label className="block text-sm font-medium text-muted-foreground mb-2">
            {usesSneakylinkWrapper ? 'Your short link code' : 'Your affiliate code'}
          </label>
          <div className="relative">
            <input
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={!isEditing || isLoading}
              className={`w-full px-3 py-2 border ${
                error ? "border-red-500" : "border-border"
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 bg-card text-center font-medium text-lg`}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            {isCheckingCode && (
              <p className="text-muted-foreground text-sm mt-1">
                Checking availability...
              </p>
            )}
          </div>
        </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {attributionMethod === 'SNEAKYLINK' && !usesSneakylinkWrapper ? (
            <Button
              onClick={() => handleSave()}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? "Processing..." : "Join Program"}
            </Button>
          ) : !isEditing ? (
            <>
              <Button
                onClick={() => handleSave()}
                disabled={isLoading || !!error}
                className="flex-1"
              >
                {isLoading ? "Processing..." : "Join Now"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
                className="flex-1"
              >
                Edit Code
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setIsEditing(false);
                handleSave();
              }}
              disabled={isLoading || !!error || isCheckingCode}
              className="w-full"
            >
              {isLoading ? "Processing..." : "Save & Join"}
            </Button>
          )}
        </div>
      </div>

      {/* Shown when the backend refused the join because it would move the
          affiliate out of another program of this brand. Confirming re-submits
          the same join with confirmSwitch. */}
      {switchPrompt && (
        <SwitchProgramModal
          isOpen
          brandName={brandName || "this brand"}
          from={switchPrompt.from}
          to={switchPrompt.to}
          requiresApproval={!switchPrompt.to.automaticApproval}
          isSubmitting={isLoading}
          onConfirm={() => handleSave(true)}
          onClose={() => setSwitchPrompt(null)}
        />
      )}
    </div>
  );
}
